/**
 * Dedupes async route loader work (getComponent / getChildRoutes side effects).
 * Prevents duplicate API calls when effects re-run or route trees briefly remount.
 */
const cache = new Map()
const inflight = new Map()

export function loadRouteOnce(key, loader) {
  if (cache.has(key)) {
    return Promise.resolve(cache.get(key))
  }
  if (inflight.has(key)) {
    return inflight.get(key)
  }

  const promise = Promise.resolve()
    .then(loader)
    .then(result => {
      cache.set(key, result)
      inflight.delete(key)
      return result
    })
    .catch(err => {
      inflight.delete(key)
      throw err
    })

  inflight.set(key, promise)
  return promise
}

export function clearRouteLoaderCache(key) {
  cache.delete(key)
  inflight.delete(key)
}

/** Clears all cached/in-flight route loaders (call on logout so study fetch side effects re-run). */
export function clearAllRouteLoaderCache() {
  cache.clear()
  inflight.clear()
}

function componentCacheKey(routeScope, routeKey) {
  return `component:${routeScope ?? 'route'}:${routeKey}`
}

/**
 * Resolve getComponent for a module + URL. In-flight requests are deduped only;
 * completed components are not cached so a stale getComponent cannot poison
 * another module's cache entry during route transitions.
 */
export function resolveGetComponent(routeScope, routeKey, getComponent, v3Location) {
  const key = componentCacheKey(routeScope, routeKey)

  if (inflight.has(key)) {
    return inflight.get(key)
  }

  const promise = new Promise((resolve, reject) => {
    getComponent(v3Location, (err, component) => {
      if (err) reject(err)
      else resolve(component)
    })
  }).finally(() => {
    inflight.delete(key)
  })

  inflight.set(key, promise)
  return promise
}

export function resolveExpandedChildren(expansionKey, loader) {
  return loadRouteOnce(`children:${expansionKey}`, loader)
}
